import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import type { SessionDetail, ErrorLog, GrammarPattern, VocabItem, Exercise, Usage, FocusOutcome } from "../../types";
import GenerationLoader from "../GenerationLoader";
import FeedbackModal from "../FeedbackModal";

function formatDuration(s: number | null): string {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

function SkeletonCard() {
  return (
    <div className="bg-surface border border-border-soft rounded-[10px] px-3.5 py-3 min-w-0">
      <div className="h-7 w-10 rounded-md bg-surface-raised animate-pulse mb-2" />
      <div className="h-2 w-16 rounded bg-surface-raised animate-pulse" />
    </div>
  );
}

function StatCard({
  label,
  value,
  valueColor,
  active = false,
  onClick,
  tooltip,
}: {
  label: string;
  value: string | number;
  valueColor?: string;
  active?: boolean;
  onClick?: () => void;
  tooltip?: string;
}) {
  return (
    <div
      className={`bg-surface border rounded-[10px] px-3.5 py-3 min-w-0 text-left transition-colors duration-200 group relative ${
        onClick ? "cursor-pointer" : "cursor-default"
      } ${active ? "border-warning bg-warning/10" : "border-border-soft"}`}
      onClick={onClick}
    >
      <div className="text-[1.5rem] font-display font-semibold tracking-[-0.02em] leading-tight tabular-nums" style={valueColor ? { color: valueColor } : { color: "var(--ink)" }}>
        {value}
      </div>
      <div className="text-[0.625rem] mt-1 text-text-tertiary uppercase tracking-[0.12em] font-medium flex items-center gap-1">
        {label}
        {tooltip && (
          <span className="text-text-tertiary opacity-50 cursor-help" title={tooltip}>ⓘ</span>
        )}
      </div>
      {tooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 card p-2.5 text-[0.68rem] text-text-secondary leading-relaxed opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
          {tooltip}
        </div>
      )}
    </div>
  );
}

const dotColors: Record<string, string> = {
  grammar: "#3B82C4",
  vocabulary: "#C89B3C",
  pronunciation: "#D46A5A",
  syntax: "#5E8F87",
  default: "#D46A5A",
};

function SpeakerIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

function speakItalian(text: string, setSpeaking: (v: boolean) => void) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "it-IT";
  utterance.rate = 0.9;
  const itVoice = window.speechSynthesis.getVoices().find((v) => v.lang.startsWith("it"));
  if (itVoice) utterance.voice = itVoice;
  utterance.onstart = () => setSpeaking(true);
  utterance.onend = () => setSpeaking(false);
  utterance.onerror = () => setSpeaking(false);
  window.speechSynthesis.speak(utterance);
}

function grammarLabel(value: string) {
  return value.replace(/_/g, " ").replace(/^./, (letter) => letter.toUpperCase());
}

function patternLabel(pattern: Pick<GrammarPattern, "grammar_topic_label" | "verb_form_subtype_label" | "grammar_error_form_label">) {
  return [pattern.grammar_topic_label, pattern.verb_form_subtype_label, pattern.grammar_error_form_label].filter(Boolean).join(" · ");
}

type GrammarPatternIdentity = Pick<ErrorLog, "grammar_topic" | "grammar_error_form" | "verb_form_subtype">;

function samePattern(left: GrammarPatternIdentity, right: GrammarPatternIdentity) {
  return left.grammar_topic === right.grammar_topic
    && left.grammar_error_form === right.grammar_error_form
    && left.verb_form_subtype === right.verb_form_subtype;
}

function matchesPattern(error: ErrorLog, pattern: GrammarPattern) {
  return samePattern(error, pattern);
}

function ErrorItem({ error }: { error: ErrorLog }) {
  const dot = dotColors[error.error_type] || dotColors.default;
  const isPronunciation = error.error_type === "pronunciation";
  const grammarTags = [error.grammar_topic, error.verb_form_subtype, error.grammar_error_form].filter((tag): tag is string => !!tag);
  const [speaking, setSpeaking] = useState(false);

  return (
    <div className="bg-surface border border-border-soft rounded-[10px] px-4 py-3 text-sm animate-fade-in">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
          <span className="text-[0.65rem] uppercase tracking-[0.12em] font-semibold text-text-tertiary">
            {error.error_type}
          </span>
        </div>
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {grammarTags.map((tag) => (
            <span key={tag} className="text-[0.6rem] uppercase tracking-[0.08em] font-semibold text-accent-deep bg-accent-soft rounded-full px-2 py-0.5">
              {grammarLabel(tag)}
            </span>
          ))}
          {isPronunciation && (
            <button
              type="button"
              onClick={() => speakItalian(error.corrected_text, setSpeaking)}
              title="Hear the correct pronunciation"
              aria-label="Hear the correct pronunciation"
              className={`shrink-0 p-1 rounded-sm transition-colors duration-150 cursor-pointer hover:text-accent ${speaking ? "text-accent animate-pulse-soft" : "text-text-tertiary"}`}
            >
              <SpeakerIcon />
            </button>
          )}
        </div>
      </div>
      <div className="text-error/80 line-through decoration-error/40">{error.original_text}</div>
      <div className="text-success font-medium">{error.corrected_text}</div>
      <div className="text-text-secondary mt-1 italic text-sm leading-relaxed">{error.explanation}</div>
    </div>
  );
}

interface Props {
  sessionId: number;
  sessionNumber?: number;
  onClose: () => void;
  onDeleted?: (deletedId: number) => void;
  isTrial?: boolean;
}

function TrashIcon({ size = 18 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

function VocabRow({ vocab }: { vocab: VocabItem }) {
  const [speaking, setSpeaking] = useState(false);

  return (
    <div className="flex items-center gap-4 bg-surface border border-border-soft rounded-[10px] px-4 py-3 text-sm">
      <span className="flex-1 font-medium text-ink">{vocab.word}</span>
      <span className="text-text-tertiary text-xs">{vocab.translation}</span>
      {vocab.difficulty && (
        <span className="text-[0.6rem] uppercase tracking-[0.1em] py-1 px-2.5 rounded-full bg-accent-soft text-accent-deep font-semibold">
          {vocab.difficulty}
        </span>
      )}
      <button
        type="button"
        onClick={() => speakItalian(vocab.word, setSpeaking)}
        title="Hear pronunciation"
        aria-label={`Hear pronunciation of ${vocab.word}`}
        className={`shrink-0 p-1 rounded-sm transition-colors duration-150 cursor-pointer hover:text-accent ${speaking ? "text-accent animate-pulse-soft" : "text-text-tertiary"}`}
      >
        <SpeakerIcon />
      </button>
    </div>
  );
}

function ExerciseReviewItem({ exercise, onRetry }: { exercise: Exercise; onRetry: () => void }) {
  const done = exercise.status === "completed";
  const wrong = !done && (exercise.attempt_count ?? 0) > 0;
  // Correct answers open their solution by default; unsolved ones stay hidden
  // so reviewing the tab doesn't spoil a retry.
  const [revealed, setRevealed] = useState(done);
  const dot = dotColors[exercise.category] || dotColors.default;

  const pill = done
    ? { cls: "bg-success/15 text-success", label: "✓ Correct" }
    : wrong
    ? { cls: "bg-error/15 text-error", label: "✗ Incorrect" }
    : { cls: "bg-surface2 text-text-tertiary", label: "Pending" };

  return (
    <div className="bg-surface border border-border-soft rounded-[10px] px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
          <span className="text-[0.65rem] uppercase tracking-[0.12em] font-semibold text-text-tertiary">
            {exercise.category}
          </span>
          <span className="text-[0.6rem] text-text-tertiary/70 lowercase">· {exercise.mode}</span>
        </div>
        <span className={`text-[0.6rem] uppercase tracking-[0.1em] py-0.5 px-2 rounded-full font-semibold ${pill.cls}`}>
          {pill.label}
        </span>
      </div>
      <div className="text-text-tertiary text-[0.8rem] mb-1">{exercise.prompt}</div>
      <div className="text-ink font-medium">{exercise.text}</div>

      <div className="mt-2 flex items-center gap-4">
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          className="text-[0.72rem] text-accent-deep hover:text-accent font-medium cursor-pointer"
        >
          {revealed ? "Hide answer" : "Show answer"}
        </button>
        {wrong && (
          <button
            type="button"
            onClick={onRetry}
            className="text-[0.72rem] text-error hover:text-error/80 font-semibold cursor-pointer"
          >
            Try again →
          </button>
        )}
      </div>

      {revealed && (
        <div className="mt-1.5 border-t border-border-soft pt-2">
          <div className="text-success font-medium">{exercise.answer}</div>
          <div className="text-text-tertiary mt-1 italic text-[0.8rem] leading-relaxed">{exercise.explanation}</div>
        </div>
      )}
    </div>
  );
}

const FOCUS_RESULT: Record<FocusOutcome["result"], { text: string; className: string }> = {
  mastered: { text: "✓ Nailed it", className: "text-success font-medium" },
  progress: { text: "Used correctly", className: "text-success" },
  slipped: { text: "Slipped again", className: "text-error" },
  not_practised: { text: "Not used", className: "text-text-tertiary" },
};

export default function SessionSidePanel({ sessionId, sessionNumber, onClose, onDeleted, isTrial }: Props) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [grammarPatternFilter, setGrammarPatternFilter] = useState<GrammarPattern | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"errors" | "transcript" | "exercises">("errors");
  const [exercises, setExercises] = useState<Exercise[] | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const loadDetail = async () => {
      try {
        const data = await api.getSession(sessionId);
        if (cancelled) return;
        setDetail(data);
        // If analysis is still running, poll every 3s until it finishes.
        if (data.session.analysis_complete === false) {
          pollTimer = setTimeout(loadDetail, 3000);
        }
      } catch (err) {
        console.error("Failed to load session detail:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    setExercises(null);
    setFilterType(null);
    setGrammarPatternFilter(null);
    loadDetail();

    (async () => {
      try {
        const ex = await api.getExercises({ sessionId, status: "all" });
        if (!cancelled) setExercises(ex);
      } catch (err) {
        console.error("Failed to load session exercises:", err);
        if (!cancelled) setExercises([]);
      }
    })();

    (async () => {
      try {
        const u = await api.getUsage();
        if (!cancelled) setUsage(u);
      } catch {
        /* refund affordance just stays hidden if usage can't load */
      }
    })();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [sessionId]);

  const practiceOnPage = () => navigate(`/exercises?session=${sessionId}`);

  const generateAndPractice = () => {
    navigate(`/exercises?session=${sessionId}&generate=true`);
  };

  const sess = detail?.session;
  const isRefunded = !!sess?.refunded_at;
  const refundsLeft = usage?.refunds_remaining ?? 0;
  // A substantive, not-yet-refunded session gets refunded on delete while the user
  // has redos left. Error-interrupted sessions are already auto-refunded.
  const willRefund = !!sess?.counted_toward_quota && !isRefunded && refundsLeft > 0;

  const exCount = exercises?.length ?? 0;
  const exPending = exercises?.filter((e) => e.status === "pending").length ?? 0;

  const selectTypeFilter = (type: string | null) => {
    setGrammarPatternFilter(null);
    setFilterType((current) => current === type ? null : type);
  };

  const selectGrammarPattern = (pattern: GrammarPattern) => {
    setFilterType(null);
    setGrammarPatternFilter((current) => current && samePattern(current, pattern) ? null : pattern);
    setActiveTab("errors");
  };

  const displayedErrors: ErrorLog[] = detail
    ? detail.errors.filter((error) => (!filterType || error.error_type === filterType)
      && (!grammarPatternFilter || matchesPattern(error, grammarPatternFilter)))
    : [];

  return (
    <>
      <div className="fixed inset-0 bg-[rgba(36,28,18,0.4)] backdrop-blur-[2px] z-[200] flex justify-end animate-fade-in" onClick={onClose}>
        <div className="w-full sm:w-[820px] sm:max-w-[95vw] h-full bg-bg sm:border-l border-border p-6 sm:p-8 overflow-y-auto animate-slide-in-right relative" onClick={(e) => e.stopPropagation()}>
          <button
            className="absolute top-5 right-6 bg-transparent border-none text-text-tertiary text-2xl cursor-pointer leading-none transition-colors duration-200 hover:text-ink p-2"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>

        {loading && <p className="text-text-tertiary italic text-sm p-12 text-center">Loading…</p>}

        {!loading && detail && (
          <>
            <h2 className="mb-1.5 mr-10">Session {sessionNumber ?? detail.session.id}</h2>
            <div className="flex gap-4 flex-wrap text-text-tertiary text-sm mb-7">
              <span>{new Date(detail.session.started_at).toLocaleString()}</span>
              {detail.session.duration_s != null && <span>{formatDuration(detail.session.duration_s)}</span>}
              {detail.session.topic && <span className="text-text-secondary italic">{detail.session.topic}</span>}
            </div>

            {detail.session.summary && (
              <>
                <h4 className="section-label mb-2.5">Summary</h4>
                <div className="card px-5 py-4 text-[0.9375rem] text-text-secondary leading-relaxed mb-7">
                  {detail.session.summary}
                </div>
              </>
            )}

            {detail.session.analysis_complete === false && (
              <GenerationLoader
                className="mb-6"
                messages={[
                  "Analyzing your session...",
                  "Reviewing grammar mistakes...",
                  "Scoring pronunciation...",
                  "Computing fluency metrics...",
                  "Finalizing feedback...",
                ]}
                subtitle="This usually takes 30–60 seconds"
              />
            )}

            <h4 className="section-label mb-3">Performance</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-6">
              {detail.session.analysis_complete === false ? (
                Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
              ) : (
                <>
                  <StatCard
                    label="Total errors"
                    value={detail.errors.length}
                    valueColor="var(--error)"
                    active={filterType === null && grammarPatternFilter === null}
                    onClick={() => selectTypeFilter(null)}
                  />
                  <StatCard
                    label="Grammar"
                    value={detail.errors.filter((e) => e.error_type === "grammar").length}
                    valueColor="#3B82C4"
                    active={filterType === "grammar" || grammarPatternFilter !== null}
                    onClick={() => selectTypeFilter("grammar")}
                  />
                  {detail.metrics && (
                    <>
                      <StatCard
                        label="Vocabulary"
                        value={detail.metrics.vocabulary_errors}
                        valueColor="#C89B3C"
                        active={filterType === "vocabulary"}
                        onClick={() => selectTypeFilter("vocabulary")}
                      />
                      <StatCard
                        label="Pronunciation"
                        value={detail.metrics.pronunciation_flags}
                        valueColor="#D46A5A"
                        active={filterType === "pronunciation"}
                        onClick={() => selectTypeFilter("pronunciation")}
                      />
                      <StatCard label="Fluency /10" value={detail.metrics.fluency_score?.toFixed?.(1) ?? "—"} valueColor="var(--success)" tooltip="Based on speaking pace (WPM), filler-word rate (ehm, allora…), and error density. Higher is more fluent." />
                      <StatCard label="Complexity /10" value={detail.metrics.complexity_score?.toFixed?.(1) ?? "—"} valueColor="var(--terracotta)" tooltip="Based on vocabulary richness (unique words / total), average sentence length, and use of subordinate clauses (che, quando, perché…)." />
                      <StatCard label="Distinct words" value={detail.metrics.unique_words_used ?? 0} tooltip="Distinct word forms used by you in this session (lowercased, punctuation stripped, no lemmatization — so parlato and parlavo count separately)." />
                      <StatCard label="Sentences" value={detail.metrics.sentences_produced ?? 0} />
                    </>
                  )}
                </>
              )}
            </div>

            {detail.focus?.result && detail.focus.result.length > 0 && (
              <div className="card px-5 py-4 mb-6">
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <h4 className="section-label">{detail.focus.mode === "focus" ? "Focus session" : "Top mistakes"}</h4>
                  {detail.focus.mastered.length > 0 && (
                    <span className="text-xs font-medium text-success">
                      Nailed it: {detail.focus.mastered.length}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {detail.focus.result.map((item) => (
                    <div key={item.key} className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-ink min-w-0">{item.label}</span>
                      <span className={`text-xs whitespace-nowrap ${FOCUS_RESULT[item.result].className}`}>
                        {FOCUS_RESULT[item.result].text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.grammar_summary.length > 0 && (
              <div className="card px-5 py-4 mb-6">
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <h4 className="section-label">Main grammar patterns</h4>
                  <span className="text-[0.65rem] text-text-tertiary">Click to review</span>
                </div>
                <div className="flex flex-col divide-y divide-border-soft">
                  {detail.grammar_summary.slice(0, 3).map((pattern) => {
                    const selected = !!grammarPatternFilter && samePattern(grammarPatternFilter, pattern);
                    return (
                      <button
                        key={`${pattern.grammar_topic}-${pattern.grammar_error_form}-${pattern.verb_form_subtype ?? ""}`}
                        type="button"
                        onClick={() => selectGrammarPattern(pattern)}
                        aria-pressed={selected}
                        className={`-mx-2 px-2 py-2 first:-mt-2 last:-mb-2 rounded-md flex items-center justify-between gap-4 text-left transition-colors cursor-pointer ${selected ? "bg-accent-soft" : "hover:bg-surface-raised"}`}
                      >
                        <span className="text-sm text-ink">{patternLabel(pattern)}</span>
                        <span className="text-sm font-display font-semibold tabular-nums text-accent-deep whitespace-nowrap">
                          {pattern.count} {pattern.count === 1 ? "error" : "errors"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-0 mb-4 mt-7 border-b border-border">
              <button
                className={`px-4 py-2 text-[0.6875rem] uppercase tracking-[0.14em] font-semibold border-b-2 -mb-px transition-colors duration-150 ${
                  activeTab === "errors"
                    ? "border-warning text-warning"
                    : "border-transparent text-text-tertiary hover:text-ink"
                }`}
                onClick={() => setActiveTab("errors")}
              >
                Feedback ({displayedErrors.length})
              </button>
              <button
                className={`px-4 py-2 text-[0.6875rem] uppercase tracking-[0.14em] font-semibold border-b-2 -mb-px transition-colors duration-150 ${
                  activeTab === "exercises"
                    ? "border-warning text-warning"
                    : "border-transparent text-text-tertiary hover:text-ink"
                }`}
                onClick={() => setActiveTab("exercises")}
              >
                Exercises{exCount > 0 ? ` (${exCount})` : ""}
              </button>
              <button
                className={`px-4 py-2 text-[0.6875rem] uppercase tracking-[0.14em] font-semibold border-b-2 -mb-px transition-colors duration-150 ${
                  activeTab === "transcript"
                    ? "border-warning text-warning"
                    : "border-transparent text-text-tertiary hover:text-ink"
                }`}
                onClick={() => setActiveTab("transcript")}
              >
                Transcript
              </button>
            </div>

            {activeTab === "errors" && grammarPatternFilter && (
              <button
                type="button"
                onClick={() => setGrammarPatternFilter(null)}
                className="mb-3 inline-flex items-center gap-2 rounded-full bg-accent-soft px-3 py-1.5 text-xs font-medium text-accent-deep cursor-pointer hover:bg-accent-soft/70"
              >
                Showing {patternLabel(grammarPatternFilter)}
                <span aria-hidden="true">×</span>
              </button>
            )}

            {activeTab === "errors" && (
              <>
                {displayedErrors.length === 0 ? (
                  <p className="text-text-tertiary italic text-sm">
                    {grammarPatternFilter
                      ? `No corrections match ${patternLabel(grammarPatternFilter)}.`
                      : filterType ? `No ${filterType} errors.` : "No errors logged — bravissimo."}
                  </p>
                ) : (
                  <div className="flex flex-col gap-2 mb-5">
                    {displayedErrors.map((e) => (
                      <ErrorItem key={e.id} error={e} />
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === "transcript" && (
              <div className="mb-5 flex flex-col gap-4">
                {detail.session.raw_transcript ? (() => {
                  type Turn = { speaker: string; lines: string[] };
                  const turns: Turn[] = [];
                  for (const raw of detail.session.raw_transcript.split("\n")) {
                    const colon = raw.indexOf(":");
                    if (colon === -1) continue;
                    const speaker = raw.slice(0, colon).trim();
                    const text = raw.slice(colon + 1).trim();
                    if (!text) continue;
                    if (turns.length > 0 && turns[turns.length - 1].speaker === speaker) {
                      turns[turns.length - 1].lines.push(text);
                    } else {
                      turns.push({ speaker, lines: [text] });
                    }
                  }
                  return turns.map((turn, i) => {
                    const isElena = turn.speaker === "Elena";
                    return isElena ? (
                      <div key={i} className="self-start max-w-[88%]">
                        <div className="font-display font-semibold tracking-[-0.01em] text-success text-[0.8125rem] mb-1">Elena</div>
                        <div className="bg-[#152a1e] rounded-2xl rounded-tl-sm px-4 py-3">
                          <p className="text-[0.9375rem] leading-7 text-text">{turn.lines.join(" ")}</p>
                        </div>
                      </div>
                    ) : (
                      <div key={i} className="self-end max-w-[80%] flex flex-col items-end">
                        <div className="font-display font-semibold tracking-[-0.01em] text-text-tertiary text-[0.8125rem] mb-1">You</div>
                        <div className="bg-[#26262b] rounded-2xl rounded-tr-sm px-4 py-3">
                          <p className="text-[0.9375rem] leading-6 text-text">{turn.lines.join(" ")}</p>
                        </div>
                      </div>
                    );
                  });
                })() : (
                  <p className="text-text-tertiary italic text-sm">No transcript available.</p>
                )}
              </div>
            )}

            {activeTab === "exercises" && (
              <div className="mb-5">
                {exercises === null ? (
                  <p className="text-text-tertiary italic text-sm">Loading…</p>
                ) : exercises.length === 0 ? (
                  <div className="card px-6 py-9 text-center">
                    <p className="text-text-tertiary text-sm leading-relaxed max-w-sm mx-auto mb-5">
                      No practice exercises for this session yet. Turn its mistakes into written and spoken drills.
                    </p>
                    <button onClick={generateAndPractice} className="btn-primary text-sm">
                      Create practice exercises
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="text-xs text-text-tertiary">
                        {exCount - exPending} of {exCount} correct
                      </span>
                      {exPending > 0 && (
                        <button onClick={practiceOnPage} className="btn-primary text-xs px-4 py-2">
                          {exPending === exCount ? "Practice" : "Continue"} ({exPending})
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      {exercises.map((ex) => (
                        <ExerciseReviewItem key={ex.id} exercise={ex} onRetry={practiceOnPage} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {(detail.vocabulary.length > 0 || (detail.metrics?.new_words_count ?? 0) > 0) && (
              <>
                <h4 className="section-label mt-7 mb-3 flex items-baseline gap-2">
                  <span>Vocabulary ({detail.vocabulary.length})</span>
                  {(detail.metrics?.new_words_count ?? 0) > 0 && (
                    <span className="text-text-tertiary opacity-70 normal-case tracking-normal font-normal text-[0.72rem]">
                      · {detail.metrics!.new_words_count} new {detail.metrics!.new_words_count === 1 ? "word" : "words"} this session
                    </span>
                  )}
                </h4>
                <div className="flex flex-col gap-2">
                  {detail.vocabulary.map((v) => (
                    <VocabRow key={v.id} vocab={v} />
                  ))}
                </div>
              </>
            )}

            {exercises !== null && (
              <div className="mt-8 rounded-xl border border-warning/50 bg-warning/20 px-5 py-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-ink">
                    {exercises.length === 0
                      ? "Turn your mistakes into practice"
                      : exPending === 0
                      ? "All exercises complete"
                      : `${exPending} exercise${exPending === 1 ? "" : "s"} left to practice`}
                  </p>
                  {exercises.length === 0 && (
                    <p className="text-xs text-text-tertiary mt-0.5">Drills built from this session's errors</p>
                  )}
                </div>
                {exPending === 0 && exercises.length > 0 ? (
                  <span className="text-xs font-semibold text-success shrink-0">✓ Done</span>
                ) : (
                  <button
                    onClick={exercises.length === 0 ? generateAndPractice : practiceOnPage}
                    className="btn-primary text-xs px-4 py-2 shrink-0"
                  >
                    {exercises.length === 0 ? "Create exercises" : "Practice now"}
                  </button>
                )}
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-border-soft flex justify-between items-center gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                {isTrial ? (
                  <span className="text-xs text-text-tertiary italic">Session deletion is not available during trial</span>
                ) : (
                  <button
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium bg-transparent border-none text-text-tertiary transition-colors duration-200 cursor-pointer hover:text-error disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={() => setConfirmOpen(true)}
                    disabled={deleting}
                  >
                    <TrashIcon size={14} />
                    Delete
                  </button>
                )}
                {isRefunded && (
                  <span className="text-xs text-text-tertiary italic">
                    {sess?.refund_reason === "error"
                      ? "Refunded — interrupted by an error"
                      : "Refunded — didn't count toward your plan"}
                  </span>
                )}
              </div>
              <button className="btn-quiet text-xs px-4 py-2 shrink-0" onClick={onClose}>
                Close
              </button>
            </div>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setFeedbackOpen(true)}
                className="text-xs text-text-tertiary hover:text-accent-deep bg-transparent border-none cursor-pointer transition-colors duration-150 underline-offset-2 hover:underline"
              >
                Something off with this session? Tell us
              </button>
            </div>
          </>
        )}
      </div>
    </div>

    {confirmOpen && (
      <div className="fixed inset-0 bg-[rgba(36,28,18,0.45)] z-[300] flex items-center justify-center animate-fade-in" onClick={() => setConfirmOpen(false)}>
        <div className="card max-w-[420px] w-[90vw] px-8 py-9 text-center animate-scale-in" onClick={(e) => e.stopPropagation()}>
          <h3 className="mb-2">Delete session {sessionId}?</h3>
          <p className="text-text-secondary text-[0.9375rem] mb-6 leading-relaxed">
            This permanently removes the session, all feedback, metrics, and vocabulary.
            {willRefund && (
              <>
                {" "}It will be refunded, so it won't count toward your plan limit
                ({refundsLeft} {refundsLeft === 1 ? "refund" : "refunds"} left this period).
              </>
            )}
          </p>
          <div className="flex justify-center gap-3">
            <button className="btn-quiet" onClick={() => setConfirmOpen(false)}>
              Cancel
            </button>
            <button
              className="inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-medium bg-error !text-white border border-error transition-all duration-200 cursor-pointer hover:bg-[#93312a] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={async () => {
                setDeleting(true);
                try {
                  await api.deleteSession(sessionId);
                  onDeleted?.(sessionId);
                } catch (err) {
                  console.error("Failed to delete session:", err);
                  alert("Failed to delete session.");
                } finally {
                  setDeleting(false);
                  setConfirmOpen(false);
                }
              }}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      </div>
    )}

    {feedbackOpen && <FeedbackModal sessionId={sessionId} onClose={() => setFeedbackOpen(false)} />}
    </>
  );
}
